import { type ComponentProps, type ReactNode, useId, useState } from 'react'
import { agentEditorCopy } from '@/copy'
import type { ModelProfileInfo, ReasoningControlInfo } from '@/types/api'
import type { ReasoningSetting } from '@/types/editor'
import type { JsonValue } from '@/types/generated/serde_json/JsonValue'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { CheckboxField, Field, Input, Select } from '@/ui/Field/Field'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'

interface ReasoningSectionProps {
  /** The model's profile on its provider; undefined until both are set and
   * it has loaded. */
  profile: ModelProfileInfo | undefined
  /** As `FormState.reasoning`; `null` or absent is the model's default. */
  value: ReasoningSetting | null | undefined
  onChange: (v: ReasoningSetting | null) => void
  /** The agent's old extended-thinking setting didn't carry over exactly. */
  notice?: boolean
  /** The profile request failed. */
  error?: Error | null
  /** Provider and model are set and the profile is still loading. */
  loading?: boolean
  /** Try the profile request again. */
  onRetry?: () => void
}

const OFF = '__off'

/** Whether the model takes `setting`, as the server's save check decides:
 * exactly one key, the control's own name, and one of its values. */
function fits(
  control: ReasoningControlInfo,
  setting: ReasoningSetting,
): boolean {
  const entries = Object.entries(setting)
  if (entries.length !== 1) return false
  const [key, value] = entries[0]
  if (!('name' in control) || key !== control.name) return false
  if (control.type === 'toggle')
    return value === control.on || value === control.off
  if (value === false) return control.can_disable
  if (control.type === 'levels')
    return typeof value === 'string' && control.values.includes(value)
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= control.min &&
    (control.max == null || value <= control.max)
  )
}

/** The agent's reasoning setting, in the controls its model's profile has:
 * levels, a token budget, or an on/off switch, with "off" where the model
 * can turn reasoning off. The server checks it again on save. */
export function ReasoningSection({
  profile,
  value,
  onChange,
  notice,
  error,
  loading,
  onRetry,
}: ReasoningSectionProps) {
  const hintId = useId()
  const entries = value ? Object.entries(value) : []
  // Anything but one key isn't a setting (the alert below says so).
  const [key, current] =
    entries.length === 1 ? entries[0] : [undefined, undefined]
  const control = profile?.reasoning
  const name = control && 'name' in control ? control.name : undefined
  const showSkeleton = useSkeletonDelay(!!loading && (!profile || !control))
  const set = (v: JsonValue) => {
    if (name) onChange({ [name]: v })
  }
  const summary = !value
    ? agentEditorCopy.reasoning.modelDefault
    : key === undefined
      ? JSON.stringify(value)
      : agentEditorCopy.reasoning.setting(key, JSON.stringify(current))

  let body: ReactNode
  if ((!profile || !control) && error) {
    body = (
      <Stack gap="space-8">
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            <Text>
              {agentEditorCopy.reasoning.profileFailed(error.message)}
            </Text>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {agentEditorCopy.retry}
              </Button>
            )}
          </Inline>
        </Alert>
        {value && (
          <Inline gap="space-8">
            <Button
              variant="ghost"
              ground="surface"
              onClick={() => onChange(null)}
            >
              {agentEditorCopy.reasoning.useModelDefault}
            </Button>
          </Inline>
        )}
      </Stack>
    )
  } else if ((!profile || !control) && loading) {
    body = (
      <Stack gap="space-8" aria-busy="true">
        {showSkeleton && <Skeleton lines={2} ground="surface" />}
      </Stack>
    )
  } else if (!profile || !control) {
    body = (
      <Text variant="caption" tone="text-mute">
        {agentEditorCopy.reasoning.pickModel}
      </Text>
    )
  } else if (control.type === 'none' || control.type === 'always_on') {
    body = (
      <Text variant="caption" tone="text-mute">
        {profile.source === 'none'
          ? agentEditorCopy.reasoning.noProfile
          : control.type === 'always_on'
            ? agentEditorCopy.reasoning.alwaysOn
            : agentEditorCopy.reasoning.noControl}
      </Text>
    )
  } else if (control.type === 'levels') {
    const selected =
      current === false ? OFF : typeof current === 'string' ? current : ''
    // `none` already turns reasoning off; `off` stays for a stored false.
    const offerOff =
      control.can_disable &&
      (current === false || !control.values.includes('none'))
    body = (
      <Field id="af-reasoning-level" label={control.name}>
        <Select
          value={selected}
          onChange={(e) => {
            const v = e.target.value
            if (!v) onChange(null)
            else set(v === OFF ? false : v)
          }}
        >
          <option value="">
            {control.thinks_by_default
              ? control.default
                ? agentEditorCopy.reasoning.modelDefaultWith(control.default)
                : agentEditorCopy.reasoning.modelDefault
              : agentEditorCopy.reasoning.modelDefaultNoReasoning}
          </option>
          {control.values.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
          {offerOff && (
            <option value={OFF}>{agentEditorCopy.reasoning.off}</option>
          )}
        </Select>
      </Field>
    )
  } else if (control.type === 'budget') {
    const off = current === false
    body = (
      <Stack gap="space-16">
        <Field
          id="af-reasoning-budget"
          label={control.name}
          help={
            control.max != null
              ? agentEditorCopy.reasoning.range(control.min, control.max)
              : agentEditorCopy.reasoning.atLeast(control.min)
          }
        >
          <BudgetInput
            value={typeof current === 'number' ? current : undefined}
            min={control.min}
            max={control.max ?? undefined}
            disabled={off && control.can_disable}
            onCommit={(v) => (v === undefined ? onChange(null) : set(v))}
          />
        </Field>
        {control.can_disable && (
          <CheckboxField
            id="af-reasoning-off"
            label={agentEditorCopy.reasoning.off}
            ground="surface"
            checked={off}
            onChecked={(c) => (c ? set(false) : onChange(null))}
          />
        )}
      </Stack>
    )
  } else {
    const on =
      key === undefined ? control.thinks_by_default : current === control.on
    body = (
      <Stack gap="space-8">
        <CheckboxField
          id="af-reasoning-toggle"
          label={control.name}
          ground="surface"
          checked={on}
          onChecked={() => set(on ? control.off : control.on)}
          inputProps={{ 'aria-describedby': hintId }}
        />
        <Inline gap="space-8" align="baseline">
          <Text variant="caption" tone="text-mute" id={hintId}>
            {agentEditorCopy.reasoning.modelDefaultToggle(
              control.thinks_by_default,
            )}
          </Text>
          {key !== undefined && (
            <Button
              variant="ghost"
              ground="surface"
              onClick={() => onChange(null)}
            >
              {agentEditorCopy.reasoning.useDefault}
            </Button>
          )}
        </Inline>
      </Stack>
    )
  }

  return (
    <Stack gap="space-16">
      <SectionHeader>
        {agentEditorCopy.reasoning.heading(summary)}
      </SectionHeader>
      {notice && (
        <Alert tone="warning">{agentEditorCopy.reasoning.notice}</Alert>
      )}
      {profile && control && value && !fits(control, value) && (
        <Stack gap="space-8">
          <Alert tone="danger">
            {agentEditorCopy.reasoning.unsupported(summary)}
          </Alert>
          <Inline gap="space-8">
            <Button
              variant="ghost"
              ground="surface"
              onClick={() => onChange(null)}
            >
              {agentEditorCopy.reasoning.useModelDefault}
            </Button>
          </Inline>
        </Stack>
      )}
      {body}
    </Stack>
  )
}

/** A token budget, committed (and so checked) when the field loses focus
 * rather than on every keystroke. Other props (the id and description
 * `Field` sets on its control) pass through to the input. */
function BudgetInput({
  value,
  min,
  max,
  disabled,
  onCommit,
  ...rest
}: Omit<
  ComponentProps<'input'>,
  'value' | 'min' | 'max' | 'onChange' | 'onBlur'
> & {
  value: number | undefined
  min: number
  max: number | undefined
  disabled: boolean
  onCommit: (v: number | undefined) => void
}) {
  const shown = value === undefined ? '' : String(value)
  const [draft, setDraft] = useState(shown)
  // A value set from outside (off, a reset) replaces the draft.
  const [synced, setSynced] = useState(shown)
  if (synced !== shown) {
    setSynced(shown)
    setDraft(shown)
  }
  return (
    <Input
      {...rest}
      type="number"
      min={min}
      max={max}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft.trim() ? Number(draft) : undefined)}
      placeholder={agentEditorCopy.reasoning.modelDefault}
      disabled={disabled}
    />
  )
}
